import { useRef } from 'react';
import { ModalBackdrop } from '../ModalBackdrop';
import { WithDropdown } from '../WithDropdown';
import { OptionalPortal } from '../OptionalPortal';
import { CustomSelectBaseProps } from './customSelectBaseTypes';
import { CustomSelectBaseDropdownContent } from './CustomSelectBaseDropdownContent';
import { SelectComponentButton } from './SelectComponentButton';

/**
 * This custom select component offers a flexible solution for building complex
 * dropdown menus. It's designed to accept any content you provide as children,
 * making it easy to create unique dropdown layouts. A built-in search field
 * enables filtering of the options, while advanced keyboard navigation ensures
 * a smooth user experience. Users can easily scroll through options, and select
 * their choice using just the keyboard.
 * See e.g. the ConnectedTestOptions component for an example of how to use this.
 */
export const CustomSelectBase = <T = any,>({
  alignMenuTo = 'left',
  disablePortal,
  onMenuOpen,
  onMenuClose,
  clickNamespace,
  modalBoxVerticalOffset = 4,
  children,
  closeOnSelect = false,
  placeholderText = '',
  onOptionsChanged = () => {},
  actionNamespace,
  optionsChangedAction,
  onFilterChanged,
  isFindMaxHeight,
  defaultValue,
  hasClearButton = false,
  onClear,
  selectButton,
  minDropdownSize,
  hasSearchInputInDropdown = false,
  additionalSearchControls,
  i18nLocale,
}: CustomSelectBaseProps<T>) => {
  const forceAlignMenuToRight = alignMenuTo !== 'left';
  const refClearButton = useRef<HTMLDivElement>(null);

  return (
    <WithDropdown<HTMLButtonElement>
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
    >
      {(openModal, closeModal, isOpen, getHostBox, hostRef) => {
        const onClick = (e: React.MouseEvent) => {
          if (hasClearButton && onClear && refClearButton.current) {
            if (refClearButton.current.contains(e.target as Node)) {
              // Clicked on clear button, do not open dropdown
              onClear();
              return;
            }
          }
          openModal();
        };

        const hasCustomSelectButton = Boolean(selectButton || minDropdownSize);

        return (
          <>
            <SelectComponentButton
              selectButton={selectButton}
              onClick={onClick}
              hostRef={hostRef}
              isOpen={isOpen}
              defaultValue={defaultValue}
              placeholderText={placeholderText}
              hasClearButton={hasClearButton}
              refClearButton={refClearButton}
              hasSearchInputInDropdown={hasSearchInputInDropdown}
              i18nLocale={i18nLocale}
            />
            {isOpen && (
              <OptionalPortal disablePortal={disablePortal}>
                <ModalBackdrop closeModal={closeModal} isKeepOpen={true}>
                  {(addKeyboardListener, removeKeyboardListener) => (
                    <CustomSelectBaseDropdownContent<T>
                      clickNamespace={clickNamespace}
                      getHostBox={getHostBox}
                      addKeyboardListener={addKeyboardListener}
                      removeKeyboardListener={removeKeyboardListener}
                      placeholderText={placeholderText}
                      forceAlignMenuToRight={forceAlignMenuToRight}
                      modalBoxVerticalOffset={modalBoxVerticalOffset}
                      onOptionsChanged={value => {
                        onOptionsChanged(value);
                        if (closeOnSelect) {
                          closeModal();
                        }
                      }}
                      actionNamespace={actionNamespace}
                      optionsChangedAction={optionsChangedAction}
                      onFilterChanged={onFilterChanged}
                      isFindMaxHeight={isFindMaxHeight}
                      defaultValue={defaultValue}
                      onClear={onClear}
                      closeModal={closeModal}
                      minDropdownSize={minDropdownSize}
                      hasCustomSelectButton={hasCustomSelectButton}
                      hasSearchInputInDropdown={hasSearchInputInDropdown}
                      additionalSearchControls={additionalSearchControls}
                      i18nLocale={i18nLocale}
                    >
                      {children}
                    </CustomSelectBaseDropdownContent>
                  )}
                </ModalBackdrop>
              </OptionalPortal>
            )}
          </>
        );
      }}
    </WithDropdown>
  );
};
